/**
 * The print options dialog — one control per declared option, then confirm.
 *
 * It renders a closed set of primitives so it can stay generic across every
 * resource that prints, and it stays OPEN on failure: an error that closed
 * the dialog would throw away the choices the operator just made, right when
 * they need correcting.
 */
import { useResourceKit } from '@resource-kit/core/react';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../primitives/Modal';
import { Button } from '../../primitives/Button';
import { Checkbox } from '../../primitives/Checkbox';
import type {
  PrintOptionDef,
  PrintOptionValue,
  PrintOptionValues,
} from '@resource-kit/controllers/react';

interface PrintOptionsDialogProps {
  open: boolean;
  defs: readonly PrintOptionDef[];
  values: PrintOptionValues;
  onChange: (id: string, value: PrintOptionValue) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Message from a failed run; keeps the dialog open rather than closing it. */
  error: string | null;
  /** A run is in flight — blocks a second confirm. */
  busy: boolean;
}

export function PrintOptionsDialog({
  open,
  defs,
  values,
  onChange,
  onConfirm,
  onCancel,
  error,
  busy,
}: PrintOptionsDialogProps) {
  const { t } = useResourceKit();

  return (
    <Modal isOpen={open} onClose={onCancel} size="sm">
      <ModalHeader onClose={onCancel}>
        <ModalTitle>{t('rk.print.options.title')}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-3">
          {defs.map((def) => (
            <label key={def.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={values[def.id] === true}
                onCheckedChange={(checked) => onChange(def.id, checked === true)}
              />
              <span>{t(def.labelKey)}</span>
            </label>
          ))}
          {error !== null && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onCancel}>
          {t('rk.ui.cancel')}
        </Button>
        <Button onClick={onConfirm} disabled={busy}>
          {t('rk.print.options.confirm')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
